import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { Attachment, UUID } from './types';
import { Vector2Tuple } from 'three';
import { IconProps } from '@tabler/icons-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';
import { PlaygroundFilling } from '@/api/PlaygroundFillingsApi';
import { TransformControlsMode } from 'three/examples/jsm/controls/TransformControls.js';
import { Project, ProjectContact } from '@/api/ProjectApi';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { ProductModule } from '@/api/ProductModuleApi';
import { generateProjectPayload } from '@/utils/utilities';
import { SerializedRenderer } from '@/renderer/types';

export enum PlaygroundShapeType {
	RECTANGLE = 'rectangle',
	CIRCLE = 'circle',
	CUSTOM = 'custom',
}

interface BaseShape {
	id: UUID;
	name: string;
	Icon: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
}

interface CircleShape extends BaseShape {
	type: PlaygroundShapeType.CIRCLE;
	radius: number;
	vertices?: never;
}

interface RectangleShape extends BaseShape {
	type: PlaygroundShapeType.RECTANGLE;
	vertices: Vector2Tuple[];
	radius?: never;
}

interface CustomShape extends BaseShape {
	type: PlaygroundShapeType.CUSTOM;
	vertices: Vector2Tuple[];
	radius?: never;
}

export type PlaygroundShape = CircleShape | RectangleShape | CustomShape;

export type PlaygroundTexture = {
	id: UUID;
	image: string;
	name: string;
};

export enum CameraType {
	PERSPECTIVE = 'perspective',
	ORTHOGRAPHIC = 'orthographic',
}

export enum CreatorStep {
	SHAPES,
	MODULES,
	CONTACT,
}

export interface CreatorCustomBackground {
	attachment: Attachment | null;
	dimensions: Vector2Tuple;
	rotation: number;
}

export interface CreatorMeasurement {
	id: UUID;
	name: string;
}

export interface CreatorScreenshotCamera {
	id: UUID;
	name: string;
}

export interface ModuleContextMenuData {
	moduleRefId: UUID | null;
	position: Vector2Tuple;
	type: 'product' | 'landscape';
}

export interface CreatorOptions {
	isPreviewMode: boolean;
	selectedShape: UUID | null;
	selectedPlaygroundFilling: PlaygroundFilling | null;
	isMobileAsideOpened: boolean;
	currentStep: CreatorStep;
	isLoading: boolean;
	isDrawing: boolean;
	screenshotPopoverOpened: boolean;
	activeCamera: CameraType;
	areCodesVisible: boolean;
	isMeasuring: boolean;
	creatorMeasurements: CreatorMeasurement[];
	selectedCreatorModule: UUID | null;
	infoProductModule: null | ProductModule;
	instructionModalOpened: boolean;
	productModulesDrawerOpened: boolean;
	landscapeModulesDrawerOpened: boolean;
	successDrawerOpened: boolean;
	playgroundSurfaceArea: number;
	customBackground: CreatorCustomBackground;
	transformControlsMode: Exclude<TransformControlsMode, 'scale'>;
	screenshotCameras: CreatorScreenshotCamera[];
	moduleContextMenuData: ModuleContextMenuData;
}

export interface CreatorRendererOptions {
	loading: boolean;
	zoomLevel: number;
	distance: number;
}

export interface CreatorState {
	creatorProductModules: CreatorProductModule[];
	creatorLandscapeModules: CreatorLandscapeModule[];
	contact: ProjectContact;
	renderer: CreatorRendererOptions;
	options: CreatorOptions;
	name: string;
	project: Omit<Project, 'configuration'> | null;
	isAdmin: boolean;
}

export type SerializedProject = ReturnType<typeof generateProjectPayload>;

export interface SerializedProjectConfiguration {
	creatorProductModules: { refId: UUID; productModule: { id: UUID } }[];
	creatorLandscapeModules: { refId: UUID; landscapeModule: { id: UUID } }[];
	isActive: boolean;
	options: {
		state: {
			renderer: CreatorRendererOptions;
			options: CreatorOptions;
		};
		renderer: SerializedRenderer;
	};
}
